/**
 * 작업 분류(TBM 분류 축)의 유효값 파생 — 결정 기록
 * `docs/decisions/2026-09-02-tbm-work-type-axis-redesign.md` 규약 ③④.
 *
 * 값은 세 벌이고 레벨이 다르다. 분석값을 사람값으로 덮어쓰지 않으므로(불변 기록),
 * "지금 무엇이 쓰이는가"는 매번 사다리로 파생해야 한다.
 *
 *   1. 사람 확정값   finding.confirmedWorkCategory   ← 발견사항별 오버라이드
 *   2. 사진 레벨 사람값 photo.processType             ← 사람이 사진 전체에 지정
 *   3. 분석값        finding.analyzedWorkCategory    ← 목록에서 고른 불변 기록
 *
 * 2층에 equipment.processType을 함께 두는 이유: 결정 기록 본문은 `photo.processType`만
 * 적었지만, 실제 코드는 두 곳 모두 사진의 공종/공정을 `photo.processType || equipment.processType`으로
 * 본다 — 화면 표시(`PhotoMetaSection`의 `effectiveProcessType`)와 분석 문맥 구성
 * (서버 `photo-analysis.processor.ts`). 여기서 equipment를 빼면 사용자 눈에 보이는 공정과
 * 이견 배지가 어긋난다(대상에서 온 공정인데 이견이 안 뜨거나, 반대로 뜬다).
 */

/**
 * 빈 문자열·공백만인 값은 '없음'으로 본다.
 * `??`만 쓰면 `''`가 유효값 행세를 해 사다리가 거기서 멈춘다.
 */
function present(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export interface WorkCategorySources {
  /** 발견사항 사람 확정값 */
  confirmedWorkCategory?: string | null;
  /** 사진에 직접 지정된 공종/공정 */
  photoProcessType?: string | null;
  /** 사진의 대상(설비)에 지정된 공종/공정 */
  equipmentProcessType?: string | null;
  /** 분석이 후보 목록에서 고른 값 (불변) */
  analyzedWorkCategory?: string | null;
}

/** 사진 레벨의 사람 입력 공정 — 사진 직접 지정이 대상(설비) 기본값을 이긴다. */
export function resolvePhotoProcessType(
  sources: Pick<WorkCategorySources, 'photoProcessType' | 'equipmentProcessType'>,
): string | null {
  return present(sources.photoProcessType) ?? present(sources.equipmentProcessType);
}

/** 지금 이 발견사항에 실제로 쓰이는 작업 분류. 아무 층도 없으면 null(미분류). */
export function resolveEffectiveWorkCategory(sources: WorkCategorySources): string | null {
  return (
    present(sources.confirmedWorkCategory) ??
    resolvePhotoProcessType(sources) ??
    present(sources.analyzedWorkCategory)
  );
}

/**
 * 이견 = 분석값이 있는데 유효값이 그와 다른 상태 (규약 ④).
 * 유효값이 분석값으로 떨어진 경우는 이견이 아니다 — 어긋난 것이 없다.
 */
export function getWorkCategoryDissent(
  sources: WorkCategorySources,
): { analyzed: string; effective: string } | null {
  const analyzed = present(sources.analyzedWorkCategory);
  if (!analyzed) return null;

  const effective = resolveEffectiveWorkCategory(sources);
  if (!effective || effective === analyzed) return null;

  return { analyzed, effective };
}
